import React, { useEffect, useRef, useState } from 'react';
import { useFramework, useScale } from '@/framework';
import type { ILayerProps } from '@/version-types';
import { VIDEO_IDENTIFIER_ICON } from '../assets';
import { type EdgelessMediaLayer } from '../edgeless';
import { EdgelessVideoControlBarV2 } from '../video-control-bar';
import styles from './styles.module.less';

type PlayVideoOperateProps = {
    layer: ILayerProps;
    restoreCoverOnPause?: boolean;
    showControlBarWhenPlaying?: boolean;
};

/**
 * 提供视频暂停态的透明播放点击区
 *
 * @param props - 视频图层、暂停封面恢复策略和播放态控制栏策略
 * @returns 视频播放点击区、控制栏或空节点
 */
export const PlayVideoOperate: React.FC<PlayVideoOperateProps> = React.memo(
    ({ layer, restoreCoverOnPause = true, showControlBarWhenPlaying = true }) => {
        const framework = useFramework();
        const scale = useScale();
        const playRequestRef = useRef(0);
        const playPendingRef = useRef(false);

        const iconWidth = VIDEO_IDENTIFIER_ICON.sizeRatio * Math.max(layer.width, layer.height) * scale;
        const iconHeight = iconWidth;
        const layerId = layer.id;
        const obj = framework.service.edgelessCanvas.getByLayerId(layerId) as EdgelessMediaLayer | null;
        const events = framework.service.events;
        const [isPlaying, setIsPlaying] = useState(obj?.isPlaying ?? false);
        const [isPending, setIsPending] = useState(false);

        useEffect(() => {
            playRequestRef.current += 1;
            playPendingRef.current = false;
            setIsPending(false);
            setIsPlaying(obj?.isPlaying ?? false);
            if (!obj) return;

            const handler = obj.onChange(({ isPlaying }) => {
                if (!isPlaying && restoreCoverOnPause) {
                    obj.mediaElement.setRenderCover();
                }
                setIsPlaying(isPlaying);
            });
            return () => {
                playRequestRef.current += 1;
                playPendingRef.current = false;
                events.off(handler);
            };
        }, [events, obj, restoreCoverOnPause]);

        if (!obj) return null;

        if (isPlaying) {
            return showControlBarWhenPlaying ? <EdgelessVideoControlBarV2 layerId={layer.id} /> : null;
        }

        return (
            <div
                aria-disabled={isPending}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.stopPropagation();
                    if (playPendingRef.current) return;

                    const requestId = ++playRequestRef.current;
                    playPendingRef.current = true;
                    setIsPending(true);
                    void obj
                        .play()
                        .catch((error) => {
                            console.error('PlayVideoOperate play error', error);
                        })
                        .finally(() => {
                            if (playRequestRef.current === requestId) {
                                playPendingRef.current = false;
                                setIsPending(false);
                            }
                        });
                }}
                className={styles.playVideoOperate}
                style={{ width: iconWidth, height: iconHeight }}
            ></div>
        );
    },
);

/**
 * 组合可编辑态视频的透明播放点击区和底部控制栏
 *
 * @param props - 当前视频图层
 * @returns 可编辑态视频操作节点
 */
export const EditableVideoOperate: React.FC<{ layer: ILayerProps }> = ({ layer }) => (
    <>
        <PlayVideoOperate layer={layer} restoreCoverOnPause={false} showControlBarWhenPlaying={false} />
        <EdgelessVideoControlBarV2 layerId={layer.id} />
    </>
);
